import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { RefreshPlaceFlow } from '@elara/client-core/lib/refresh-place';
import type { Place } from '@elara/client-core/models/PlaceModel';

import { useEffect } from 'react';

import { refreshPlaceOnce } from '@elara/client-core/lib/refresh-place';

import { useApp } from '#states/hooks.js';

interface FeaturesShape {
  places?: PlacesContainer;
  flows?: { refreshPlace?: RefreshPlaceFlow };
}

/**
 * Triggers a background refresh of place data only when the locally cached
 * copy is stale. The server runs the same staleness check, but doing it
 * client-side too avoids issuing dozens of redundant flow calls when a trip
 * with many places loads.
 */
export function useRefreshPlace(placeId: string | undefined): void {
  const { features } = useApp('features');
  const { places, flows } = features as FeaturesShape;
  const refreshPlace = flows?.refreshPlace;

  const handle = placeId && places ? places.ClientPlace.get({ id: placeId }) : undefined;
  const place = handle?.data as Place | undefined;
  const isStale = place?.isStale ?? false;

  useEffect(() => {
    if (!placeId || !isStale || !refreshPlace) return;
    void refreshPlaceOnce(refreshPlace, placeId);
  }, [placeId, isStale, refreshPlace]);
}
